import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogIn,
  UserPlus,
  Users,
  Calendar,
  Trophy,
  Award,
  CheckCircle,
  ArrowRight,
  Mail,
  Lock,
  BookOpen,
  Zap,
  Shield
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

interface GuideStep {
  number: number;
  title: string;
  description: string;
  icon: React.ElementType;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeGuide, setActiveGuide] = useState(0);

  const guideSteps: GuideStep[] = [
    {
      number: 1,
      title: 'Register as Volunteer',
      description: 'Create your account with basic information. You\'ll receive a unique member ID starting with 155.',
      icon: UserPlus
    },
    {
      number: 2,
      title: 'Wait for Approval',
      description: 'Admin will review and approve your registration. You\'ll be notified once approved.',
      icon: Shield
    },
    {
      number: 3,
      title: 'Login to Portal',
      description: 'Use your email and password to access your volunteer dashboard with points, events, and QR code.',
      icon: LogIn
    },
    {
      number: 4,
      title: 'Attend Events',
      description: 'Show your QR code at events for attendance tracking. Earn points and climb the leaderboard!',
      icon: Trophy
    }
  ];

  const features = [
    { icon: Users, text: 'Unique Member ID (155XX)' },
    { icon: Calendar, text: 'Event Management & QR Codes' },
    { icon: Trophy, text: 'Points & Achievement Levels' },
    { icon: Award, text: 'Leaderboard Rankings' }
  ];

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use AuthContext login method
      const result = await login(email, password);

      if (result.success) {
        toast.success('Welcome back, Admin! 👋');
        // Navigate to admin dashboard
        navigate('/admin/dashboard');
      } else {
        toast.error(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      {/* Header */}
      <header className="bg-white border-b border-secondary-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center">
            <div className="bg-[#0D5128] p-2 rounded-xl flex items-center justify-center">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-8 w-auto object-contain" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Split Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* LEFT SIDE - Login Form */}
          <div>
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-2 sm:mb-3">
                Welcome Back!
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-secondary-600">
                Login to access your volunteer dashboard
              </p>
            </div>

            {/* Login Form Card */}
            <div className="card bg-white shadow-lg sm:shadow-xl">
              <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn btn-primary text-base sm:text-lg py-3 sm:py-4 shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Logging in...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <LogIn className="h-5 w-5" />
                      Login
                    </div>
                  )}
                </button>
              </form>

              {/* Register Link */}
              <div className="mt-8 pt-6 border-t border-secondary-200">
                <p className="text-center text-secondary-600 mb-4">
                  Don't have an account yet?
                </p>
                <Link
                  to="/register"
                  className="block w-full btn bg-secondary-100 text-secondary-900 hover:bg-secondary-200 border-2 border-secondary-300 no-underline"
                >
                  <UserPlus className="h-5 w-5" />
                  Register as Volunteer
                </Link>
              </div>

              {/* Demo Credentials */}
              {/* <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-xs font-semibold text-primary-900 mb-2">Demo Credentials:</p>
                <div className="text-xs text-primary-700 space-y-1">
                  <p><strong>Admin:</strong> admin@hammadfoundation.org / admin123</p>
                  <p className="text-primary-600">Register as volunteer to test member login</p>
                </div>
              </div> */}
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-secondary-200">
                    <div className="bg-primary-100 rounded-lg p-2 flex-shrink-0">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-secondary-900 leading-tight">{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT SIDE - Interactive User Guide */}
          <div>
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 flex-shrink-0" />
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-secondary-900">
                  How It Works
                </h2>
              </div>
              <p className="text-sm sm:text-base text-secondary-600">
                Follow these simple steps to get started with the Hammad Foundation Volunteer Portal
              </p>
            </div>

            {/* Interactive Guide Steps */}
            <div className="space-y-3 sm:space-y-4">
              {guideSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = activeGuide === index;

                return (
                  <div
                    key={index}
                    onClick={() => setActiveGuide(index)}
                    className={`card cursor-pointer transition-all duration-300 ${
                      isActive
                        ? 'bg-primary-600 text-white shadow-lg sm:shadow-xl sm:scale-105'
                        : 'bg-white hover:shadow-lg hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Step Number */}
                      <div
                        className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-base sm:text-lg ${
                          isActive
                            ? 'bg-white text-primary-600'
                            : 'bg-primary-100 text-primary-600'
                        }`}
                      >
                        {step.number}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-primary-600'}`} />
                          <h3 className={`text-base sm:text-lg font-bold ${isActive ? 'text-white' : 'text-secondary-900'}`}>
                            {step.title}
                          </h3>
                        </div>
                        <p className={`text-xs sm:text-sm leading-relaxed ${isActive ? 'text-primary-100' : 'text-secondary-600'}`}>
                          {step.description}
                        </p>
                      </div>

                      {/* Arrow Icon - Hide on small mobile */}
                      {isActive && (
                        <ArrowRight className="hidden sm:flex flex-shrink-0 h-5 w-5 sm:h-6 sm:w-6 text-white animate-pulse" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Key Benefits */}
            <div className="mt-6 sm:mt-8 card bg-gradient-to-br from-primary-600 to-primary-700 text-white">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                Key Benefits
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm">Unique member ID (155XX format) generated automatically</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm">Personal QR code for quick event check-ins</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm">Earn points and achievements for participation</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm">Track your progress on the leaderboard</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm">View upcoming events and your attendance history</span>
                </li>
              </ul>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mt-6 sm:mt-8">
              <div className="card text-center bg-white p-3 sm:p-4 lg:p-6">
                <div className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-primary-600 mb-0.5 sm:mb-1">10K+</div>
                <div className="text-[10px] sm:text-xs text-secondary-600">Members</div>
              </div>
              <div className="card text-center bg-white p-3 sm:p-4 lg:p-6">
                <div className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-primary-600 mb-0.5 sm:mb-1">155XX</div>
                <div className="text-[10px] sm:text-xs text-secondary-600">ID Format</div>
              </div>
              <div className="card text-center bg-white p-3 sm:p-4 lg:p-6">
                <div className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-primary-600 mb-0.5 sm:mb-1">24/7</div>
                <div className="text-[10px] sm:text-xs text-secondary-600">Access</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-secondary-200 mt-8 sm:mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 text-center">
          <p className="text-xs sm:text-sm text-secondary-600">
            &copy; 2026 Hammad Foundation. All rights reserved.
          </p>
          <p className="text-[10px] sm:text-xs text-secondary-500 mt-1">
            Digital Transformation Initiative - Volunteer Portal v3.0
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
